import type { CssKeyframe } from './pose';
import { readable, shade, withAlpha } from './palette';
import type { FxAnim, FxContext, FxKind, FxNode } from './fx';
import {
  BACK,
  OUT,
  P_COLOR,
  P_COLOR2,
  P_COLOR3,
  P_COUNT,
  P_OPACITY,
  P_TARGET,
  P_TEXT,
  P_X,
  P_Y,
  color,
  fitFont,
  full,
  lbl,
  num,
  px,
  r2,
  rng,
  str,
  unit,
} from './fx-kit';
import {
  P_LONGTEXT,
  P_SIZE,
  P_TURNS,
  arrive,
  clamp,
  clamp01,
  countUp,
  deg,
  glyphs,
  linesOf,
  monoText,
  onOff,
  opt,
  reel,
  windowAnim,
} from './fx-reel';

/**
 * Bannermatic's own motion design graphics: liquid blobs, 3D particle forms, extruded type, data
 * widgets (rings, lines, equalizers, tickers), a split-flap board, warp speed, CRT and light
 * leaks, tunnels, sunbursts, countdowns, a spinning sticker, a demo cursor, shape morphs and
 * text looks (RGB glitch, neon). Pure data like every fx kind.
 */

const text = (ctx: FxContext, fallback = 'Hello') =>
  str(ctx, 'text', ctx.text || fallback)
    .replace(/\s+/g, ' ')
    .trim();

const headline = (ctx: FxContext, f: number, c: string): Record<string, string | number> => ({
  fontFamily: ctx.font.family,
  fontWeight: ctx.font.weight,
  fontStyle: ctx.font.style,
  textTransform: ctx.font.transform,
  fontSize: px(f),
  lineHeight: '1',
  letterSpacing: '-0.03em',
  whiteSpace: 'nowrap',
  color: c,
});

const centre = (ctx: FxContext) => ({
  cx: (ctx.canvas.width * clamp(num(ctx, 'x', 50), 0, 100)) / 100,
  cy: (ctx.canvas.height * clamp(num(ctx, 'y', 50), 0, 100)) / 100,
});

const pop = (delay: number, duration = 600): FxAnim => ({
  delay,
  duration,
  fill: 'both',
  easing: BACK,
  keyframes: [
    { opacity: 0, transform: 'scale(0)' },
    { opacity: 1, transform: 'scale(1)' },
  ],
});

/** A point at the centre that holds children (they are placed relative to it). */
const pivot = (
  x: number,
  y: number,
  children: FxNode[],
  anims: FxAnim[] = [],
  extra: Record<string, string | number> = {},
): FxNode => ({
  style: {
    position: 'absolute',
    left: px(x),
    top: px(y),
    width: '0px',
    height: '0px',
    ...extra,
  },
  children,
  anims,
});

const spinAnim = (D: number, turns: number, delay = 0): FxAnim => ({
  delay,
  duration: Math.max(16, D - delay),
  fill: 'both',
  easing: 'linear',
  keyframes: [{ transform: 'rotate(0deg)' }, { transform: `rotate(${deg(turns * 360)})` }],
});

const circlePath = (cx: number, cy: number, r: number) =>
  `M ${r2(cx)} ${r2(cy - r)} A ${r2(r)} ${r2(r)} 0 1 1 ${r2(cx - 0.01)} ${r2(cy - r)}`;

/** A list of numbers from a text parameter ("3, 5, 4"). */
function numbers(ctx: FxContext, key: string, fallback: number[], max = 24): number[] {
  const list = String(ctx.params[key] ?? '')
    .split(/[,;\s]+/)
    .map(Number)
    .filter((v) => Number.isFinite(v))
    .slice(0, max);
  return list.length >= 2 ? list : fallback;
}

// ---------------------------------------------------------------- patterns

const BLOB_SHAPES = [
  '42% 58% 70% 30% / 45% 45% 55% 55%',
  '70% 30% 46% 54% / 30% 39% 61% 70%',
  '50% 50% 34% 66% / 56% 68% 32% 44%',
  '38% 62% 63% 37% / 41% 44% 56% 59%',
];

const blobs: FxKind = {
  id: 'blobs',
  label: lbl(
    'Liquid blobs',
    'Рідкі плями',
    'Płynne plamy',
    'Pete lichide',
    'Manchas líquidas',
    'Жидкие кляксы',
  ),
  category: 'pattern',
  z: 'back',
  duration: 5000,
  params: [
    P_COUNT(5, 1, 12),
    P_SIZE(45, 5, 120),
    {
      key: 'blur',
      type: 'number',
      default: 0,
      min: 0,
      max: 120,
      label: lbl('Softness', 'М’якість', 'Miękkość', 'Moliciune', 'Suavidad', 'Мягкость'),
    },
    P_COLOR('color', 'accent'),
    P_COLOR2('accent2'),
    P_COLOR3('light'),
    P_OPACITY(1),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const rnd = rng(ctx.seed);
    const n = Math.round(clamp(num(ctx, 'count', 5), 1, 12));
    const S = (Math.min(W, H) * clamp(num(ctx, 'size', 45), 5, 120)) / 100;
    const blur = num(ctx, 'blur', 0) * unit(ctx);
    const colors = [
      color(ctx, 'color', 'accent'),
      color(ctx, 'color2', 'accent2'),
      color(ctx, 'color3', 'light'),
    ];
    const a = clamp(num(ctx, 'opacity', 1), 0.05, 1);
    return Array.from({ length: n }, (_, i) => {
      const s = S * (0.55 + rnd() * 0.7);
      const x = rnd() * W - s / 2;
      const y = rnd() * H - s / 2;
      const c = colors[i % 3];
      const path = Array.from({ length: 4 }, (_, k) => ({
        dx: (rnd() - 0.5) * S * 0.6,
        dy: (rnd() - 0.5) * S * 0.6,
        rot: (rnd() - 0.5) * 120,
        sc: 0.85 + rnd() * 0.3,
        shape: BLOB_SHAPES[(i + k) % BLOB_SHAPES.length],
      }));
      path.push(path[0]);
      return {
        style: {
          position: 'absolute',
          left: px(x),
          top: px(y),
          width: px(s),
          height: px(s),
          background: `radial-gradient(circle at 32% 30%, ${shade(c, 0.35)}, ${c} 62%, ${shade(c, -0.2)})`,
          borderRadius: BLOB_SHAPES[i % BLOB_SHAPES.length],
          opacity: a,
          ...(blur > 0 ? { filter: `blur(${px(blur)})` } : {}),
        },
        anims: [
          pop(Math.min(i * 90, D * 0.3), Math.min(900, D * 0.5)),
          {
            delay: 0,
            duration: D,
            fill: 'both',
            easing: 'linear',
            keyframes: path.map((p, k) => ({
              borderRadius: p.shape,
              translate: `${px(p.dx)} ${px(p.dy)}`,
              rotate: deg(p.rot),
              scale: String(r2(p.sc)),
              offset: k / (path.length - 1),
              easing: 'ease-in-out',
            })),
          },
        ],
      };
    });
  },
};

/** Points of a 3D form, radius 1. */
function formPoints(shape: string, n: number): Array<[number, number, number]> {
  const out: Array<[number, number, number]> = [];
  if (shape === 'cube') {
    const k = Math.max(3, Math.round(Math.cbrt(n)));
    for (let i = 0; i < k; i++)
      for (let j = 0; j < k; j++)
        for (let l = 0; l < k; l++) {
          const v = (q: number) => (q / (k - 1)) * 2 - 1;
          out.push([v(i) * 0.75, v(j) * 0.75, v(l) * 0.75]);
        }
    return out;
  }
  if (shape === 'helix') {
    for (let i = 0; i < n; i++) {
      const t = i / n;
      const a = t * Math.PI * 6 + (i % 2) * Math.PI;
      out.push([Math.cos(a) * 0.55, t * 2 - 1, Math.sin(a) * 0.55]);
    }
    return out;
  }
  // Fibonacci sphere
  const g = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / Math.max(1, n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    out.push([Math.cos(g * i) * r, y, Math.sin(g * i) * r]);
  }
  return out;
}

const particles3d: FxKind = {
  id: 'particles-3d',
  label: lbl(
    '3D particle form',
    '3D-форма з частинок',
    'Forma 3D z cząstek',
    'Formă 3D din particule',
    'Forma 3D de partículas',
    '3D-фигура из частиц',
  ),
  category: 'pattern',
  z: 'front',
  duration: 4000,
  params: [
    {
      key: 'shape',
      type: 'select',
      default: 'sphere',
      label: lbl('Form', 'Форма', 'Forma', 'Formă', 'Forma', 'Форма'),
      options: [
        opt('sphere', lbl('Sphere', 'Сфера', 'Sfera', 'Sferă', 'Esfera', 'Сфера')),
        opt('cube', lbl('Cube', 'Куб', 'Sześcian', 'Cub', 'Cubo', 'Куб')),
        opt(
          'helix',
          lbl(
            'Double helix',
            'Подвійна спіраль',
            'Podwójna helisa',
            'Dublu helix',
            'Doble hélice',
            'Двойная спираль',
          ),
        ),
      ],
    },
    P_COUNT(220, 20, 400),
    P_SIZE(32, 5, 60),
    P_TURNS(0.6),
    P_X,
    P_Y,
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const D = ctx.duration;
    const shape = str(ctx, 'shape', 'sphere');
    const n = Math.round(clamp(num(ctx, 'count', 220), 20, 400));
    const R =
      (Math.min(ctx.canvas.width, ctx.canvas.height) * clamp(num(ctx, 'size', 32), 5, 60)) / 100;
    const turns = clamp(num(ctx, 'turns', 0.6), -5, 5);
    const { cx, cy } = centre(ctx);
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const pts = formPoints(shape, n);
    const S = clamp(Math.round(Math.abs(turns) * 36), 12, 72);
    const tilt = (18 * Math.PI) / 180;
    const f = R * 3.2;
    const d = Math.max(2, R * 0.022);
    const dots: FxNode[] = pts.map(([x0, y0, z0], i) => {
      const keyframes: CssKeyframe[] = [];
      for (let s = 0; s <= S; s++) {
        const phi = (turns * 2 * Math.PI * s) / S;
        const x1 = x0 * Math.cos(phi) + z0 * Math.sin(phi);
        const z1 = -x0 * Math.sin(phi) + z0 * Math.cos(phi);
        const y2 = y0 * Math.cos(tilt) - z1 * Math.sin(tilt);
        const z2 = y0 * Math.sin(tilt) + z1 * Math.cos(tilt);
        const k = f / (f - z2 * R);
        keyframes.push({
          transform: `translate(${px(x1 * R * k)}, ${px(y2 * R * k)}) scale(${r2(k)})`,
          opacity: r2(0.25 + 0.75 * clamp01((z2 + 1) / 2)),
          offset: s / S,
        });
      }
      return {
        style: {
          position: 'absolute',
          left: px(-d / 2),
          top: px(-d / 2),
          width: px(d),
          height: px(d),
          borderRadius: '50%',
          background: i % 9 === 0 ? c2 : c,
        },
        anims: [{ delay: 0, duration: D, fill: 'both', easing: 'linear', keyframes }],
      };
    });
    return [pivot(cx, cy, dots, [pop(0, 900)])];
  },
};

const tunnel: FxKind = {
  id: 'tunnel',
  label: lbl(
    'Infinite tunnel',
    'Нескінченний тунель',
    'Nieskończony tunel',
    'Tunel infinit',
    'Túnel infinito',
    'Бесконечный туннель',
  ),
  category: 'pattern',
  z: 'back',
  duration: 4000,
  params: [
    {
      key: 'shape',
      type: 'select',
      default: 'square',
      label: lbl('Shape', 'Форма', 'Kształt', 'Formă', 'Forma', 'Форма'),
      options: [
        opt('square', lbl('Square', 'Квадрат', 'Kwadrat', 'Pătrat', 'Cuadrado', 'Квадрат')),
        opt('circle', lbl('Circle', 'Коло', 'Koło', 'Cerc', 'Círculo', 'Круг')),
        opt('diamond', lbl('Diamond', 'Ромб', 'Romb', 'Romb', 'Rombo', 'Ромб')),
      ],
    },
    P_COUNT(10, 3, 24),
    {
      key: 'speed',
      type: 'number',
      default: 1,
      min: 0.2,
      max: 4,
      step: 0.1,
      label: lbl('Speed', 'Швидкість', 'Szybkość', 'Viteză', 'Velocidad', 'Скорость'),
    },
    P_TURNS(0.15),
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
    P_OPACITY(0.7),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const n = Math.round(clamp(num(ctx, 'count', 10), 3, 24));
    const shape = str(ctx, 'shape', 'square');
    const P = clamp(3000 / clamp(num(ctx, 'speed', 1), 0.2, 4), 400, D);
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const a = clamp(num(ctx, 'opacity', 0.7), 0.05, 1);
    const S = Math.hypot(W, H);
    const t = Math.max(2, 3 * unit(ctx));
    const rings: FxNode[] = Array.from({ length: n }, (_, i) => ({
      style: {
        position: 'absolute',
        left: px(-S / 2),
        top: px(-S / 2),
        width: px(S),
        height: px(S),
        border: `${px(t)} solid ${i % 4 === 0 ? c2 : c}`,
        borderRadius: shape === 'circle' ? '50%' : '0px',
        boxSizing: 'border-box',
      },
      anims: [
        {
          delay: (-P * i) / n,
          duration: P,
          iterations: Math.max(1, Math.ceil(D / P) + 1),
          fill: 'both',
          easing: 'cubic-bezier(0.55, 0, 1, 0.45)',
          keyframes: [
            { transform: `scale(0.01)${shape === 'diamond' ? ' rotate(45deg)' : ''}`, opacity: 0 },
            {
              transform: `scale(0.12)${shape === 'diamond' ? ' rotate(45deg)' : ''}`,
              opacity: 1,
              offset: 0.25,
            },
            { transform: `scale(1.2)${shape === 'diamond' ? ' rotate(45deg)' : ''}`, opacity: 1 },
          ],
        },
      ],
    }));
    return [
      {
        style: full(ctx, { opacity: a }),
        children: [
          pivot(W / 2, H / 2, [pivot(0, 0, rings, [spinAnim(D, num(ctx, 'turns', 0.15))])]),
        ],
        anims: [arrive(0, 500, 0)],
      },
    ];
  },
};

const sunburst: FxKind = {
  id: 'sunburst',
  label: lbl(
    'Sunburst',
    'Сонячні промені',
    'Promienie słońca',
    'Raze de soare',
    'Rayos de sol',
    'Солнечные лучи',
  ),
  category: 'pattern',
  z: 'back',
  duration: 5000,
  params: [
    P_COUNT(16, 4, 48),
    P_TURNS(0.08),
    P_X,
    P_Y,
    P_COLOR('color', 'accent'),
    P_COLOR2('accent2'),
    P_OPACITY(0.5),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const n = Math.round(clamp(num(ctx, 'count', 16), 4, 48));
    const { cx, cy } = centre(ctx);
    const S = Math.hypot(W, H) * 2.2;
    const step = 360 / n;
    const c = color(ctx, 'color', 'accent');
    const c2 = color(ctx, 'color2', 'accent2');
    return [
      pivot(
        cx,
        cy,
        [
          {
            style: {
              position: 'absolute',
              left: px(-S / 2),
              top: px(-S / 2),
              width: px(S),
              height: px(S),
              borderRadius: '50%',
              background: `repeating-conic-gradient(${c} 0deg ${r2(step / 2)}deg, ${c2} ${r2(step / 2)}deg ${r2(step)}deg)`,
              opacity: clamp(num(ctx, 'opacity', 0.5), 0.05, 1),
            },
          },
        ],
        [spinAnim(ctx.duration, num(ctx, 'turns', 0.08)), arrive(0, 600, 0)],
      ),
    ];
  },
};

const warp: FxKind = {
  id: 'warp',
  label: lbl(
    'Warp speed',
    'Варп-стрибок',
    'Skok nadprzestrzenny',
    'Viteză warp',
    'Salto warp',
    'Варп-прыжок',
  ),
  category: 'pattern',
  z: 'front',
  duration: 1600,
  params: [
    P_COUNT(90, 10, 240),
    P_X,
    P_Y,
    P_COLOR('color', 'light'),
    P_COLOR2('accent'),
    P_OPACITY(0.9),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const rnd = rng(ctx.seed);
    const n = Math.round(clamp(num(ctx, 'count', 90), 10, 240));
    const { cx, cy } = centre(ctx);
    const R = Math.hypot(W, H) * 0.65;
    const c = color(ctx, 'color', 'light');
    const c2 = color(ctx, 'color2', 'accent');
    const u = unit(ctx);
    const P = Math.min(D, 700);
    const streaks: FxNode[] = Array.from({ length: n }, (_, i) => {
      const len = R * (0.15 + rnd() * 0.3);
      const th = Math.max(1.5, (1.5 + rnd() * 3) * u);
      return pivot(
        0,
        0,
        [
          {
            style: {
              position: 'absolute',
              left: '0px',
              top: px(-th / 2),
              width: px(len),
              height: px(th),
              borderRadius: px(th),
              background: `linear-gradient(90deg, transparent, ${i % 6 === 0 ? c2 : c})`,
              transformOrigin: '0% 50%',
            },
            anims: [
              {
                delay: -rnd() * P,
                duration: P,
                iterations: Math.max(1, Math.ceil(D / P) + 1),
                fill: 'both',
                easing: 'cubic-bezier(0.5, 0, 1, 0.5)',
                keyframes: [
                  { transform: `translateX(${px(R * 0.05)}) scaleX(0.05)`, opacity: 0 },
                  { transform: `translateX(${px(R * 0.3)}) scaleX(0.5)`, opacity: 1, offset: 0.4 },
                  { transform: `translateX(${px(R)}) scaleX(1.4)`, opacity: 1 },
                ],
              },
            ],
          },
        ],
        [],
        { transform: `rotate(${deg(rnd() * 360)})` },
      );
    });
    return [
      {
        style: full(ctx, { opacity: clamp(num(ctx, 'opacity', 0.9), 0.05, 1) }),
        children: [pivot(cx, cy, streaks)],
        anims: [
          {
            delay: 0,
            duration: D,
            fill: 'both',
            easing: 'linear',
            keyframes: [
              { opacity: 0, offset: 0 },
              { opacity: 1, offset: 0.12 },
              { opacity: 1, offset: 0.85 },
              { opacity: 0, offset: 1 },
            ],
          },
        ],
      },
    ];
  },
};

const wordWall: FxKind = {
  id: 'word-wall',
  label: lbl(
    'Word wall',
    'Стіна слів',
    'Ściana słów',
    'Zid de cuvinte',
    'Muro de palabras',
    'Стена слов',
  ),
  category: 'pattern',
  z: 'back',
  duration: 4000,
  params: [
    P_TEXT,
    P_COUNT(7, 2, 14),
    {
      key: 'tilt',
      type: 'number',
      default: -8,
      min: -45,
      max: 45,
      label: lbl(
        'Tilt, °',
        'Нахил, °',
        'Nachylenie, °',
        'Înclinare, °',
        'Inclinación, °',
        'Наклон, °',
      ),
    },
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
    P_OPACITY(0.22),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const rows = Math.round(clamp(num(ctx, 'count', 7), 2, 14));
    const word = text(ctx, 'MOTION');
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const a = clamp(num(ctx, 'opacity', 0.22), 0.05, 1);
    const span = Math.hypot(W, H) * 1.2;
    const rowH = (span / rows) * 0.9;
    const fs = rowH * 0.82;
    const upper = ctx.font.transform === 'uppercase';
    const unitW = (glyphs(word) + 2) * fs * (upper ? 0.68 : 0.6);
    const reps = Math.max(3, Math.ceil((span * 1.6) / Math.max(1, unitW)));
    const content = Array.from({ length: reps }, () => word).join('  /  ');
    const hot = Math.floor(rows / 2);
    const travel = span * 0.25;
    return [
      pivot(W / 2, H / 2, [
        {
          style: {
            position: 'absolute',
            left: px(-span / 2),
            top: px(-span / 2),
            width: px(span),
            height: px(span),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            transform: `rotate(${deg(num(ctx, 'tilt', -8))})`,
          },
          children: Array.from({ length: rows }, (_, i) => {
            const k = i % 2 ? 1 : -1;
            const isHot = i === hot;
            return {
              text: content,
              style: {
                ...headline(ctx, fs, isHot ? c2 : 'transparent'),
                height: px(rowH),
                lineHeight: px(rowH),
                marginLeft: px(-travel),
                WebkitTextStroke: isHot ? '' : `${px(Math.max(1, fs * 0.025))} ${c}`,
                opacity: isHot ? 1 : a,
              },
              anims: [
                {
                  delay: 0,
                  duration: D,
                  fill: 'both',
                  easing: 'linear',
                  keyframes: [
                    { transform: `translateX(${px(k < 0 ? 0 : -travel)})` },
                    { transform: `translateX(${px(k < 0 ? -travel : 0)})` },
                  ],
                },
              ],
            };
          }),
        },
      ]),
    ];
  },
};

// ---------------------------------------------------------------- text looks

const extrude: FxKind = {
  id: 'extrude',
  label: lbl(
    'Extruded 3D type',
    'Об’ємний 3D-текст',
    'Wytłoczony tekst 3D',
    'Text 3D extrudat',
    'Texto 3D extruido',
    'Объёмный 3D-текст',
  ),
  category: 'text',
  z: 'front',
  duration: 1800,
  params: [
    P_TEXT,
    P_COUNT(16, 3, 40),
    {
      key: 'angle',
      type: 'number',
      default: 135,
      min: 0,
      max: 360,
      label: lbl(
        'Direction, °',
        'Напрямок, °',
        'Kierunek, °',
        'Direcție, °',
        'Dirección, °',
        'Направление, °',
      ),
    },
    P_SIZE(22, 5, 50),
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const t = text(ctx, 'BOLD');
    const n = Math.round(clamp(num(ctx, 'count', 16), 3, 40));
    const upper = ctx.font.transform === 'uppercase';
    const f = fitFont(
      t.split(' '),
      W * 0.82,
      H * 0.6,
      upper,
      (Math.min(W, H) * num(ctx, 'size', 22)) / 100,
    );
    const a = (num(ctx, 'angle', 135) * Math.PI) / 180;
    const step = f * 0.012;
    const face = color(ctx, 'color', 'ink');
    const side = color(ctx, 'color2', 'accent');
    const grow = Math.min(900, D * 0.6);
    const layers: FxNode[] = [];
    for (let i = n; i >= 0; i--) {
      const dx = Math.cos(a) * step * i;
      const dy = Math.sin(a) * step * i;
      const isFace = i === 0;
      layers.push({
        style: full(ctx, { display: 'flex', alignItems: 'center', justifyContent: 'center' }),
        children: [
          {
            text: t,
            style: {
              ...headline(ctx, f, isFace ? face : shade(side, -0.45 * (i / n))),
              whiteSpace: 'normal',
              textAlign: 'center',
              lineHeight: '0.95',
              maxWidth: px(W * 0.86),
            },
          },
        ],
        anims: isFace
          ? [pop(0, 500)]
          : [
              {
                delay: 150,
                duration: grow,
                fill: 'both',
                easing: OUT,
                keyframes: [
                  { transform: 'translate(0px, 0px)' },
                  { transform: `translate(${px(dx)}, ${px(dy)})` },
                ],
              },
            ],
      });
    }
    return layers;
  },
};

const glitchText: FxKind = {
  id: 'glitch-text',
  label: lbl(
    'RGB glitch title',
    'RGB-глітч заголовок',
    'Tytuł z glitchem RGB',
    'Titlu glitch RGB',
    'Título glitch RGB',
    'RGB-глитч заголовок',
  ),
  category: 'text',
  z: 'front',
  duration: 1600,
  params: [
    P_TEXT,
    P_SIZE(20, 5, 50),
    P_COLOR('color', 'ink'),
    P_COLOR2('#FF2E63'),
    P_COLOR3('#08D9D6'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const rnd = rng(ctx.seed);
    const t = text(ctx, 'GLITCH');
    const upper = ctx.font.transform === 'uppercase';
    const f = fitFont(
      t.split(' '),
      W * 0.85,
      H * 0.6,
      upper,
      (Math.min(W, H) * num(ctx, 'size', 20)) / 100,
    );
    const steps = Math.max(4, Math.round(D / 70));
    const jitter = (amp: number, quiet: number): FxAnim => {
      const keyframes: CssKeyframe[] = [];
      for (let s = 0; s <= steps; s++) {
        const calm = s / steps > quiet && rnd() > 0.25;
        const dx = calm ? 0 : (rnd() - 0.5) * amp;
        const dy = calm ? 0 : (rnd() - 0.5) * amp * 0.3;
        const top = Math.round(rnd() * 70);
        keyframes.push({
          transform: `translate(${px(dx)}, ${px(dy)})`,
          clipPath: calm
            ? 'inset(0% 0% 0% 0%)'
            : `inset(${top}% 0% ${Math.max(0, 100 - top - 15 - Math.round(rnd() * 40))}% 0%)`,
          offset: s / steps,
          easing: 'steps(1, end)',
        });
      }
      return { delay: 0, duration: D, fill: 'both', easing: 'linear', keyframes };
    };
    const copy = (c: string, anims: FxAnim[], blend?: string): FxNode => ({
      style: full(ctx, {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(blend ? { mixBlendMode: blend } : {}),
      }),
      children: [
        {
          text: t,
          style: {
            ...headline(ctx, f, c),
            whiteSpace: 'normal',
            textAlign: 'center',
            maxWidth: px(W * 0.88),
          },
        },
      ],
      anims,
    });
    return [
      copy(color(ctx, 'color2', 'accent'), [jitter(f * 0.25, 0.55)]),
      copy(color(ctx, 'color3', 'accent2'), [jitter(f * 0.25, 0.55)]),
      copy(color(ctx, 'color', 'ink'), [jitter(f * 0.08, 0.45)]),
    ];
  },
};

const neon: FxKind = {
  id: 'neon',
  label: lbl('Neon sign', 'Неонова вивіска', 'Neon', 'Neon', 'Letrero de neón', 'Неоновая вывеска'),
  category: 'text',
  z: 'front',
  duration: 2400,
  params: [
    P_TEXT,
    P_SIZE(16, 4, 40),
    P_X,
    P_Y,
    P_COLOR('color', 'accent'),
    onOff('frame', 'on', lbl('Frame tube', 'Рамка', 'Ramka', 'Ramă', 'Marco', 'Рамка')),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const rnd = rng(ctx.seed);
    const t = text(ctx, 'OPEN');
    const upper = ctx.font.transform === 'uppercase';
    const f = fitFont(
      t.split(' '),
      W * 0.7,
      H * 0.45,
      upper,
      (Math.min(W, H) * num(ctx, 'size', 16)) / 100,
    );
    const c = color(ctx, 'color', 'accent');
    const glow = `0 0 ${px(f * 0.04)} #fff, 0 0 ${px(f * 0.1)} ${c}, 0 0 ${px(f * 0.25)} ${c}, 0 0 ${px(f * 0.5)} ${withAlpha(c, 0.6)}`;
    // Flickers while it warms up, then holds
    const keyframes: CssKeyframe[] = [];
    const warm = 14;
    for (let s = 0; s <= warm; s++)
      keyframes.push({
        opacity: s === warm ? 1 : rnd() > 0.45 ? 1 : 0.12,
        offset: (s / warm) * 0.4,
        easing: 'steps(1, end)',
      });
    keyframes.push({ opacity: 1, offset: 1 });
    const { cx, cy } = centre(ctx);
    const frame = ctx.params.frame !== 'off';
    const boxW = Math.min(W * 0.9, glyphs(t) * f * 0.62 + f * 1.2);
    const boxH = f * 1.9;
    return [
      {
        style: {
          position: 'absolute',
          left: px(clamp(cx - boxW / 2, 0, W - boxW)),
          top: px(clamp(cy - boxH / 2, 0, H - boxH)),
          width: px(boxW),
          height: px(boxH),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          ...(frame
            ? {
                border: `${px(Math.max(2, f * 0.05))} solid ${shade(c, 0.6)}`,
                borderRadius: px(f * 0.4),
                boxShadow: `0 0 ${px(f * 0.15)} ${c}, inset 0 0 ${px(f * 0.15)} ${c}`,
              }
            : {}),
        },
        anims: [{ delay: 0, duration: D, fill: 'both', easing: 'linear', keyframes }],
        children: [
          {
            text: t,
            style: {
              ...headline(ctx, f, shade(c, 0.75)),
              textShadow: glow,
              letterSpacing: '0.02em',
            },
          },
        ],
      },
    ];
  },
};

const FLAP_GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const FLAP_CYR = 'АБВГДЕЖЗИКЛМНОПРСТУФХЦЧШЩЭЮЯ';

const splitFlap: FxKind = {
  id: 'split-flap',
  label: lbl(
    'Split-flap board',
    'Табло, що гортається',
    'Tablica klapkowa',
    'Panou split-flap',
    'Panel de aeropuerto',
    'Перекидное табло',
  ),
  category: 'interface',
  z: 'front',
  duration: 2400,
  params: [
    P_LONGTEXT('NOW BOARDING\nGATE 7'),
    P_SIZE(7, 2, 20),
    P_X,
    P_Y,
    P_COLOR('color', 'light'),
    P_COLOR('bg', 'dark', lbl('Tiles', 'Плитки', 'Kafelki', 'Plăci', 'Fichas', 'Плитки')),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const rnd = rng(ctx.seed);
    const lines = linesOf(ctx, 'text', 'NOW BOARDING\nGATE 7', 4).map((l) => l.toUpperCase());
    if (!lines.length) return [];
    const cols = Math.max(...lines.map(glyphs));
    let fs = (Math.min(W, H) * num(ctx, 'size', 7)) / 100;
    fs = Math.min(fs, (W * 0.9) / (cols * 0.86), (H * 0.8) / (lines.length * 1.5));
    const cw = fs * 0.78;
    const ch = fs * 1.3;
    const gap = fs * 0.08;
    const c = color(ctx, 'color', 'light');
    const bg = color(ctx, 'bg', 'dark');
    const tile = shade(bg, 0.12);
    const ink = readable(tile, c, '#FFFFFF', '#111111');
    const pool = [...FLAP_GLYPHS, ...(lines.join('').match(/[А-ЯЁІЇЄҐ]/u) ? FLAP_CYR : '')];
    const land = D * 0.7;
    const rows: FxNode[] = lines.map((line, r) => ({
      style: { display: 'flex', gap: px(gap), justifyContent: 'center' },
      children: [...line.padEnd(cols, ' ')].map((target, i) => {
        const flips = 4 + Math.floor(rnd() * 8);
        const glyphList = [
          ...Array.from({ length: flips }, () => pool[Math.floor(rnd() * pool.length)]),
          target,
        ];
        const end = clamp(
          (land * (0.45 + 0.55 * ((i + r * 2) / (cols + lines.length * 2)))) / D,
          0.1,
          0.95,
        );
        const start = Math.max(0, end - flips * 0.035);
        const frames = glyphList.map((_, k) => ({
          at: start + ((end - start) * k) / (glyphList.length - 1),
          i: k,
        }));
        return {
          style: {
            position: 'relative',
            width: px(cw),
            height: px(ch),
            background: tile,
            borderRadius: px(fs * 0.1),
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
            color: ink,
          },
          children: <FxNode[]>[
            {
              ...reel(glyphList, ch, D, { frames }),
              style: { ...reel(glyphList, ch, D, { frames }).style, width: px(cw) },
            },
            {
              style: {
                position: 'absolute',
                left: '0px',
                right: '0px',
                top: px(ch / 2 - 1),
                height: px(Math.max(1, fs * 0.03)),
                background: withAlpha('#000000', 0.55),
              },
            },
          ],
        };
      }),
    }));
    const { cx, cy } = centre(ctx);
    const blockH = lines.length * (ch + gap * 3);
    return [
      {
        style: monoText(ctx, fs, {
          position: 'absolute',
          left: '0px',
          width: px(W),
          top: px(clamp(cy - blockH / 2, 0, H - blockH)),
          display: 'flex',
          flexDirection: 'column',
          gap: px(gap * 3),
          fontWeight: 700,
          transform: `translateX(${px(cx - W / 2)})`,
        }),
        anims: [arrive(0, 400, fs * 0.3)],
        children: rows,
      },
    ];
  },
};

// ---------------------------------------------------------------- interface & data

const donut: FxKind = {
  id: 'donut',
  label: lbl(
    'Progress ring',
    'Кільце прогресу',
    'Pierścień postępu',
    'Inel de progres',
    'Anillo de progreso',
    'Кольцо прогресса',
  ),
  category: 'interface',
  z: 'front',
  duration: 2200,
  params: [
    {
      key: 'value',
      type: 'number',
      default: 72,
      min: 0,
      max: 100,
      label: lbl('Value, %', 'Значення, %', 'Wartość, %', 'Valoare, %', 'Valor, %', 'Значение, %'),
    },
    {
      key: 'label',
      type: 'text',
      default: '',
      label: lbl('Caption', 'Підпис', 'Podpis', 'Legendă', 'Leyenda', 'Подпись'),
    },
    P_SIZE(20, 4, 45),
    P_X,
    P_Y,
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const v = Math.round(clamp(num(ctx, 'value', 72), 0, 100));
    const R = (Math.min(W, H) * clamp(num(ctx, 'size', 20), 4, 45)) / 100;
    const { cx, cy } = centre(ctx);
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const w = R * 0.16;
    const run = Math.max(300, D * 0.7);
    const d = circlePath(cx, cy, R - w / 2);
    const fs = R * 0.42;
    const h = fs * 1.02;
    const out: FxNode[] = [
      { style: full(ctx, { opacity: 0.16 }), path: { d, stroke: c, width: w, cap: 'butt' } },
      {
        style: full(ctx),
        path: { d, stroke: c2, width: w, cap: 'round' },
        anims: [
          {
            target: 'path',
            delay: 0,
            duration: run,
            fill: 'both',
            easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
            keyframes: [{ strokeDashoffset: 1 }, { strokeDashoffset: r2(1 - v / 100) }],
          },
        ],
      },
      {
        style: {
          position: 'absolute',
          left: px(cx - R),
          top: px(cy - h / 2),
          width: px(R * 2),
          height: px(h),
          display: 'flex',
          justifyContent: 'center',
          lineHeight: px(h),
          fontFamily: ctx.font.family,
          fontWeight: ctx.font.weight,
          fontSize: px(fs),
          fontVariantNumeric: 'tabular-nums',
          color: c,
          whiteSpace: 'pre',
        },
        children: [...countUp(0, v, run, D, h), { text: '%', style: { display: 'inline-block' } }],
      },
    ];
    const caption = str(ctx, 'label', '');
    if (caption)
      out.push({
        text: caption,
        style: monoText(ctx, Math.max(10, R * 0.12), {
          position: 'absolute',
          left: px(cx - R * 1.5),
          width: px(R * 3),
          top: px(Math.min(H - R * 0.2, cy + R + R * 0.18)),
          textAlign: 'center',
          color: withAlpha(c, 0.7),
          textTransform: 'uppercase',
          letterSpacing: '0.16em',
        }),
        anims: [arrive(run * 0.5, 400, R * 0.05)],
      });
    return out;
  },
};

const lineChart: FxKind = {
  id: 'line-chart',
  label: lbl(
    'Line chart',
    'Лінійний графік',
    'Wykres liniowy',
    'Grafic liniar',
    'Gráfico de líneas',
    'Линейный график',
  ),
  category: 'interface',
  z: 'front',
  duration: 2400,
  params: [
    {
      key: 'values',
      type: 'text',
      default: '2, 3, 2.5, 4, 3.6, 5.5, 5, 7.5',
      label: lbl('Values', 'Значення', 'Wartości', 'Valori', 'Valores', 'Значения'),
    },
    onOff('area', 'on', lbl('Fill', 'Заливка', 'Wypełnienie', 'Umplere', 'Relleno', 'Заливка')),
    P_SIZE(70, 10, 95),
    {
      key: 'height',
      type: 'number',
      default: 40,
      min: 5,
      max: 90,
      label: lbl('Height, %', 'Висота, %', 'Wysokość, %', 'Înălțime, %', 'Altura, %', 'Высота, %'),
    },
    P_X,
    P_Y,
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const list = numbers(ctx, 'values', [2, 3, 2.5, 4, 3.6, 5.5, 5, 7.5], 40);
    const w = (W * clamp(num(ctx, 'size', 70), 10, 95)) / 100;
    const h = (H * clamp(num(ctx, 'height', 40), 5, 90)) / 100;
    const { cx, cy } = centre(ctx);
    const x0 = clamp(cx - w / 2, 0, W - w);
    const y0 = clamp(cy - h / 2, 0, H - h);
    const lo = Math.min(...list);
    const hi = Math.max(...list);
    const pts = list.map((v, i) => [
      x0 + (w * i) / (list.length - 1),
      y0 + h - (h * (v - lo)) / Math.max(1e-6, hi - lo),
    ]);
    let d = `M ${r2(pts[0][0])} ${r2(pts[0][1])}`;
    for (let i = 1; i < pts.length; i++) {
      const [ax, ay] = pts[i - 1];
      const [bx, by] = pts[i];
      const mx = (ax + bx) / 2;
      d += ` C ${r2(mx)} ${r2(ay)}, ${r2(mx)} ${r2(by)}, ${r2(bx)} ${r2(by)}`;
    }
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const u = unit(ctx);
    const run = Math.max(300, D * 0.7);
    const out: FxNode[] = [];
    for (let g = 0; g <= 3; g++)
      out.push({
        style: {
          position: 'absolute',
          left: px(x0),
          top: px(y0 + (h * g) / 3),
          width: px(w),
          height: px(Math.max(1, u)),
          background: withAlpha(c, 0.12),
        },
      });
    if (ctx.params.area !== 'off')
      out.push({
        style: full(ctx),
        path: {
          d: `${d} L ${r2(x0 + w)} ${r2(y0 + h)} L ${r2(x0)} ${r2(y0 + h)} Z`,
          stroke: 'none',
          width: 0,
          fill: withAlpha(c2, 0.16),
        },
        anims: [
          {
            delay: run * 0.6,
            duration: Math.min(600, D * 0.3),
            fill: 'both',
            easing: OUT,
            keyframes: [{ opacity: 0 }, { opacity: 1 }],
          },
        ],
      });
    out.push({
      style: full(ctx),
      path: { d, stroke: c2, width: Math.max(2, 5 * u), cap: 'round' },
      anims: [
        {
          target: 'path',
          delay: 0,
          duration: run,
          fill: 'both',
          easing: 'cubic-bezier(0.45, 0, 0.25, 1)',
          keyframes: [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }],
        },
      ],
    });
    const [ex, ey] = pts[pts.length - 1];
    const dot = Math.max(8, 16 * u);
    out.push({
      style: {
        position: 'absolute',
        left: px(ex - dot / 2),
        top: px(ey - dot / 2),
        width: px(dot),
        height: px(dot),
        borderRadius: '50%',
        background: c2,
        boxShadow: `0 0 0 ${px(dot * 0.45)} ${withAlpha(c2, 0.25)}`,
      },
      anims: [pop(Math.min(run, D - 400), 400)],
    });
    return out;
  },
};

const equalizer: FxKind = {
  id: 'equalizer',
  label: lbl('Equalizer', 'Еквалайзер', 'Korektor', 'Egalizator', 'Ecualizador', 'Эквалайзер'),
  category: 'interface',
  z: 'front',
  duration: 4000,
  params: [
    P_COUNT(28, 4, 80),
    {
      key: 'bpm',
      type: 'number',
      default: 120,
      min: 40,
      max: 220,
      label: lbl('Tempo, BPM', 'Темп, BPM', 'Tempo, BPM', 'Tempo, BPM', 'Tempo, BPM', 'Темп, BPM'),
    },
    {
      key: 'mirror',
      type: 'select',
      default: 'off',
      label: lbl('Mirror', 'Дзеркально', 'Lustrzanie', 'Oglindit', 'Espejo', 'Зеркально'),
      options: [
        opt('off', lbl('Off', 'Вимк.', 'Wył.', 'Oprit', 'No', 'Выкл.')),
        opt('on', lbl('On', 'Увімк.', 'Wł.', 'Pornit', 'Sí', 'Вкл.')),
      ],
    },
    P_SIZE(70, 10, 100),
    {
      key: 'height',
      type: 'number',
      default: 30,
      min: 5,
      max: 90,
      label: lbl('Height, %', 'Висота, %', 'Wysokość, %', 'Înălțime, %', 'Altura, %', 'Высота, %'),
    },
    P_X,
    { ...P_Y, default: 60 },
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const rnd = rng(ctx.seed);
    const n = Math.round(clamp(num(ctx, 'count', 28), 4, 80));
    const w = (W * clamp(num(ctx, 'size', 70), 10, 100)) / 100;
    const h = (H * clamp(num(ctx, 'height', 30), 5, 90)) / 100;
    const { cx, cy } = centre(ctx);
    const mirror = ctx.params.mirror === 'on';
    const beat = 60_000 / clamp(num(ctx, 'bpm', 120), 40, 220);
    const S = clamp(Math.round(D / (beat / 2)), 2, 160);
    const gap = w / n / 4;
    const bw = (w - gap * (n - 1)) / n;
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const x0 = clamp(cx - w / 2, 0, W - w);
    // Levels per half beat: loud on the beat, a spectrum shape (more bass), some randomness
    const levels = Array.from({ length: S + 1 }, (_, s) => (s % 2 === 0 ? 1 : 0.55));
    return Array.from({ length: n }, (_, i) => {
      const band = 1 - (i / n) * 0.55;
      const keyframes: CssKeyframe[] = levels.map((lv, s) => ({
        transform: `scaleY(${r2(clamp(0.08 + lv * band * (0.35 + rnd() * 0.65), 0.05, 1))})`,
        offset: s / S,
        easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)',
      }));
      return {
        style: {
          position: 'absolute',
          left: px(x0 + i * (bw + gap)),
          top: px(mirror ? cy - h / 2 : cy - h),
          width: px(bw),
          height: px(h),
          borderRadius: px(bw / 2),
          background: i % 7 === 3 ? c2 : c,
          transformOrigin: mirror ? '50% 50%' : '50% 100%',
        },
        anims: [{ delay: 0, duration: D, fill: 'both', easing: 'linear', keyframes }],
      };
    });
  },
};

const ticker: FxKind = {
  id: 'ticker',
  label: lbl(
    'Ticker tape',
    'Біржова стрічка',
    'Pasek notowań',
    'Bandă de cotații',
    'Cinta de cotizaciones',
    'Биржевая лента',
  ),
  category: 'interface',
  z: 'front',
  duration: 6000,
  params: [
    P_LONGTEXT('LAUNCH +48%\nUSERS +12K\nCHURN -3%\nNPS +71'),
    { ...P_Y, default: 90 },
    P_SIZE(3.2, 1, 10),
    P_COLOR('bg', 'dark', lbl('Background', 'Фон', 'Tło', 'Fundal', 'Fondo', 'Фон')),
    P_COLOR('color', 'light'),
    P_COLOR2('#2BD576'),
    P_COLOR3('#FF4D4D'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const items = linesOf(ctx, 'text', 'LAUNCH +48%\nUSERS +12K', 16);
    if (!items.length) return [];
    const fs = (Math.min(W, H) * clamp(num(ctx, 'size', 3.2), 1, 10)) / 100;
    const bandH = fs * 2.2;
    const y = clamp((H * num(ctx, 'y', 90)) / 100 - bandH / 2, 0, H - bandH);
    const bg = color(ctx, 'bg', 'dark');
    const ink = readable(bg, color(ctx, 'color', 'light'), '#FFFFFF', '#111111');
    const up = color(ctx, 'color2', 'accent');
    const down = color(ctx, 'color3', 'accent2');
    const chunk: FxNode[] = items.map((it) => {
      const neg = /(^|\s)[-−–]\s?\d/.test(it);
      const pos = /(^|\s)\+\s?\d/.test(it);
      return {
        style: {
          display: 'inline-flex',
          alignItems: 'center',
          gap: px(fs * 0.5),
          marginRight: px(fs * 2.4),
        },
        children: <FxNode[]>[
          { text: it, style: { display: 'inline-block', color: ink } },
          ...(neg || pos
            ? [
                {
                  text: neg ? '▼' : '▲',
                  style: {
                    display: 'inline-block',
                    color: neg ? down : up,
                    fontSize: px(fs * 0.7),
                  },
                },
              ]
            : []),
        ],
      };
    });
    const chars = items.reduce((s, it) => s + glyphs(it) + 6, 0);
    const unitW = chars * fs * 0.6;
    const reps = Math.max(2, Math.ceil((W * 2) / Math.max(1, unitW)) + 1);
    const travel = Math.min(
      unitW * Math.max(1, Math.round(((D / 1000) * 0.12 * W) / Math.max(1, unitW))),
      unitW * (reps - 1),
    );
    return [
      {
        style: {
          position: 'absolute',
          left: '0px',
          top: px(y),
          width: px(W),
          height: px(bandH),
          background: bg,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
        },
        anims: [
          {
            delay: 0,
            duration: Math.min(500, D * 0.2),
            fill: 'both',
            easing: OUT,
            keyframes: [{ transform: 'scaleY(0)' }, { transform: 'scaleY(1)' }],
          },
        ],
        children: [
          {
            style: monoText(ctx, fs, { display: 'flex', whiteSpace: 'nowrap', fontWeight: 600 }),
            children: Array.from({ length: reps }, () => chunk).flat(),
            anims: [
              {
                delay: 0,
                duration: D,
                fill: 'both',
                easing: 'linear',
                keyframes: [
                  { transform: 'translateX(0px)' },
                  { transform: `translateX(${px(-travel)})` },
                ],
              },
            ],
          },
        ],
      },
    ];
  },
};

const countdown: FxKind = {
  id: 'countdown',
  label: lbl(
    'Countdown',
    'Зворотний відлік',
    'Odliczanie',
    'Numărătoare inversă',
    'Cuenta atrás',
    'Обратный отсчёт',
  ),
  category: 'interface',
  z: 'front',
  duration: 3000,
  params: [
    {
      key: 'from',
      type: 'number',
      default: 3,
      min: 1,
      max: 10,
      label: lbl('From', 'Від', 'Od', 'De la', 'Desde', 'От'),
    },
    P_SIZE(40, 10, 80),
    P_COLOR('color', 'ink'),
    P_COLOR2('accent'),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const n = Math.round(clamp(num(ctx, 'from', 3), 1, 10));
    const seg = D / n;
    const R = (Math.min(W, H) * clamp(num(ctx, 'size', 40), 10, 80)) / 200;
    const c = color(ctx, 'color', 'ink');
    const c2 = color(ctx, 'color2', 'accent');
    const d = circlePath(W / 2, H / 2, R);
    const out: FxNode[] = [];
    for (let i = 0; i < n; i++) {
      const from = i * seg;
      const to = i === n - 1 ? D : (i + 1) * seg;
      out.push({
        style: full(ctx),
        path: { d, stroke: c2, width: Math.max(3, R * 0.06), cap: 'round' },
        anims: [
          windowAnim(D, from, to),
          {
            target: 'path',
            delay: from,
            duration: seg,
            fill: 'both',
            easing: 'linear',
            keyframes: [{ strokeDashoffset: 0 }, { strokeDashoffset: -1 }],
          },
        ],
      });
      out.push({
        style: full(ctx, { display: 'flex', alignItems: 'center', justifyContent: 'center' }),
        anims: [windowAnim(D, from, to)],
        children: [
          {
            text: String(n - i),
            style: headline(ctx, R * 1.2, c),
            anims: [
              {
                delay: from,
                duration: Math.min(400, seg * 0.6),
                fill: 'both',
                easing: OUT,
                keyframes: [
                  { transform: 'scale(1.6)', opacity: 0 },
                  { transform: 'scale(1)', opacity: 1 },
                ],
              },
            ],
          },
        ],
      });
    }
    return out;
  },
};

const cursor: FxKind = {
  id: 'cursor',
  label: lbl(
    'Demo cursor',
    'Курсор-демо',
    'Kursor demo',
    'Cursor demo',
    'Cursor de demo',
    'Курсор-демо',
  ),
  category: 'interface',
  z: 'front',
  duration: 2000,
  params: [P_TARGET, P_COLOR('color', 'light'), P_COLOR2('accent')],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const u = unit(ctx);
    const b = ctx.box ?? { x: W * 0.45, y: H * 0.45, width: W * 0.1, height: H * 0.1 };
    const tx = b.x + b.width / 2;
    const ty = b.y + b.height / 2;
    const s = 46 * u;
    const c = color(ctx, 'color', 'light');
    const outline = readable(c, '#111111', '#FFFFFF');
    const c2 = color(ctx, 'color2', 'accent');
    const click = D * 0.62;
    const arrow = `M 0 0 L 0 ${r2(s)} L ${r2(s * 0.27)} ${r2(s * 0.74)} L ${r2(s * 0.46)} ${r2(s * 1.12)} L ${r2(s * 0.6)} ${r2(s * 1.05)} L ${r2(s * 0.42)} ${r2(s * 0.68)} L ${r2(s * 0.75)} ${r2(s * 0.68)} Z`;
    const start = [W * 1.05, H * 1.05];
    const mid = [tx + (start[0] - tx) * 0.35 + W * 0.08, ty + (start[1] - ty) * 0.6];
    const ring = Math.max(b.width, b.height) * 0.6 + 30 * u;
    return [
      {
        style: {
          position: 'absolute',
          left: px(tx - ring / 2),
          top: px(ty - ring / 2),
          width: px(ring),
          height: px(ring),
          borderRadius: '50%',
          border: `${px(Math.max(2, 4 * u))} solid ${c2}`,
          boxSizing: 'border-box',
        },
        anims: [
          {
            delay: click,
            duration: Math.min(600, D - click),
            fill: 'both',
            easing: OUT,
            keyframes: [
              { transform: 'scale(0.2)', opacity: 0.9 },
              { transform: 'scale(1)', opacity: 0 },
            ],
          },
        ],
      },
      {
        style: full(ctx, {
          filter: `drop-shadow(0 ${px(4 * u)} ${px(8 * u)} rgba(0, 0, 0, 0.35))`,
        }),
        path: { d: arrow, stroke: outline, width: Math.max(1.5, 3 * u), fill: c, cap: 'round' },
        anims: [
          {
            delay: 0,
            duration: D,
            fill: 'both',
            easing: 'linear',
            keyframes: [
              {
                transform: `translate(${px(start[0])}, ${px(start[1])}) scale(1)`,
                offset: 0,
                easing: 'cubic-bezier(0.3, 0, 0.2, 1)',
              },
              {
                transform: `translate(${px(mid[0])}, ${px(mid[1])}) scale(1)`,
                offset: 0.3,
                easing: 'cubic-bezier(0.3, 0, 0.2, 1)',
              },
              { transform: `translate(${px(tx)}, ${px(ty)}) scale(1)`, offset: click / D - 0.04 },
              { transform: `translate(${px(tx)}, ${px(ty)}) scale(0.82)`, offset: click / D },
              {
                transform: `translate(${px(tx)}, ${px(ty)}) scale(1)`,
                offset: Math.min(1, click / D + 0.08),
              },
              { transform: `translate(${px(tx)}, ${px(ty)}) scale(1)`, offset: 1 },
            ],
          },
        ],
      },
    ];
  },
};

// ---------------------------------------------------------------- accents & ambience

/** 24-point polygons of shapes, so clip-path can morph between them. */
function morphPolygon(shape: string): string {
  const N = 24;
  const pts: Array<[number, number]> = [];
  const poly = (corners: Array<[number, number]>) => {
    // Spread N points evenly along the closed outline
    const segs = corners.map((p, i) => [p, corners[(i + 1) % corners.length]] as const);
    const lens = segs.map(([a, b]) => Math.hypot(b[0] - a[0], b[1] - a[1]));
    const total = lens.reduce((s, l) => s + l, 0);
    for (let k = 0; k < N; k++) {
      let d = (total * k) / N;
      let i = 0;
      while (d > lens[i] && i < segs.length - 1) d -= lens[i++];
      const [a, b] = segs[i];
      const t = lens[i] ? d / lens[i] : 0;
      pts.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
  };
  const ring = (k: number, inner = 1, start = -90) =>
    Array.from({ length: k }, (_, i) => {
      const a = ((start + (360 * i) / k) * Math.PI) / 180;
      const r = i % 2 && inner !== 1 ? 50 * inner : 50;
      return [50 + Math.cos(a) * r, 50 + Math.sin(a) * r] as [number, number];
    });
  if (shape === 'circle')
    for (let k = 0; k < N; k++) {
      const a = ((-90 + (360 * k) / N) * Math.PI) / 180;
      pts.push([50 + Math.cos(a) * 50, 50 + Math.sin(a) * 50]);
    }
  else if (shape === 'triangle') poly(ring(3));
  else if (shape === 'square')
    poly([
      [0, 0],
      [100, 0],
      [100, 100],
      [0, 100],
    ]);
  else if (shape === 'hexagon') poly(ring(6));
  else if (shape === 'star') poly(ring(10, 0.45));
  else poly(ring(8, 0.6)); // burst
  return `polygon(${pts.map(([x, y]) => `${r2(x)}% ${r2(y)}%`).join(', ')})`;
}

const MORPH_ORDER = ['circle', 'triangle', 'square', 'star', 'hexagon', 'burst'];

const shapeMorph: FxKind = {
  id: 'shape-morph',
  label: lbl(
    'Shape morph',
    'Морфінг фігур',
    'Morfing kształtów',
    'Morfare forme',
    'Transformación de formas',
    'Морфинг фигур',
  ),
  category: 'accent',
  z: 'front',
  duration: 3000,
  params: [
    P_COUNT(4, 2, 6),
    P_SIZE(30, 4, 80),
    P_TURNS(0.5),
    P_X,
    P_Y,
    P_COLOR('color', 'accent'),
    P_COLOR2('accent2'),
    P_COLOR3('ink'),
  ],
  build(ctx): FxNode[] {
    const D = ctx.duration;
    const n = Math.round(clamp(num(ctx, 'count', 4), 2, 6));
    const S =
      (Math.min(ctx.canvas.width, ctx.canvas.height) * clamp(num(ctx, 'size', 30), 4, 80)) / 100;
    const { cx, cy } = centre(ctx);
    const colors = [
      color(ctx, 'color', 'accent'),
      color(ctx, 'color2', 'accent2'),
      color(ctx, 'color3', 'ink'),
    ];
    const turns = num(ctx, 'turns', 0.5);
    const keyframes: CssKeyframe[] = [];
    for (let i = 0; i < n; i++) {
      const hold = i / n;
      keyframes.push({
        clipPath: morphPolygon(MORPH_ORDER[i]),
        background: colors[i % 3],
        rotate: deg((turns * 360 * i) / Math.max(1, n - 1)),
        offset: r2(hold + (i ? 0.45 / n : 0)),
        easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
      });
    }
    keyframes[0].offset = 0;
    keyframes.push({ ...keyframes[keyframes.length - 1], offset: 1 });
    return [
      pivot(
        cx,
        cy,
        [
          {
            style: {
              position: 'absolute',
              left: px(-S / 2),
              top: px(-S / 2),
              width: px(S),
              height: px(S),
              background: colors[0],
            },
            anims: [{ delay: 0, duration: D, fill: 'both', easing: 'linear', keyframes }],
          },
        ],
        [pop(0, 600)],
      ),
    ];
  },
};

const sticker: FxKind = {
  id: 'sticker',
  label: lbl(
    'Spinning sticker',
    'Обертовий стікер',
    'Obracająca się naklejka',
    'Autocolant rotativ',
    'Pegatina giratoria',
    'Вращающийся стикер',
  ),
  category: 'accent',
  z: 'front',
  duration: 3000,
  params: [
    {
      ...P_TEXT,
      key: 'ring',
      default: 'NEW DROP • LIMITED •',
      label: lbl(
        'Ring text',
        'Текст по колу',
        'Tekst na obwodzie',
        'Text pe cerc',
        'Texto en círculo',
        'Текст по кругу',
      ),
    },
    {
      key: 'label',
      type: 'text',
      default: '-30%',
      label: lbl('Centre', 'У центрі', 'Środek', 'Centru', 'Centro', 'В центре'),
    },
    P_SIZE(16, 5, 40),
    { ...P_X, default: 78 },
    { ...P_Y, default: 24 },
    P_TURNS(0.3),
    P_COLOR('color', 'accent'),
    P_COLOR2('ink'),
  ],
  build(ctx): FxNode[] {
    const D = ctx.duration;
    const R =
      (Math.min(ctx.canvas.width, ctx.canvas.height) * clamp(num(ctx, 'size', 16), 5, 40)) / 100;
    const { cx, cy } = centre(ctx);
    const bg = color(ctx, 'color', 'accent');
    const ink = readable(bg, color(ctx, 'color2', 'ink'), '#FFFFFF', '#111111');
    const ringText = [
      ...`${String(ctx.params.ring ?? 'NEW DROP • LIMITED •')
        .toUpperCase()
        .trim()} `,
    ];
    const fs = R * 0.16;
    const count = ringText.length;
    const r = R * 0.8;
    const label = String(ctx.params.label ?? '').slice(0, 8);
    const burst = Array.from({ length: 32 }, (_, i) => {
      const a = (i / 32) * Math.PI * 2;
      const rr = i % 2 ? 46 : 50;
      return `${r2(50 + Math.cos(a) * rr)}% ${r2(50 + Math.sin(a) * rr)}%`;
    }).join(', ');
    return [
      pivot(
        cx,
        cy,
        [
          pivot(
            0,
            0,
            [
              {
                style: {
                  position: 'absolute',
                  left: px(-R),
                  top: px(-R),
                  width: px(R * 2),
                  height: px(R * 2),
                  background: bg,
                  clipPath: `polygon(${burst})`,
                },
              },
              ...ringText.map((ch, i) => ({
                text: ch,
                style: {
                  position: 'absolute',
                  left: px(-fs / 2),
                  top: '0px',
                  width: px(fs),
                  height: px(fs),
                  lineHeight: '1',
                  textAlign: 'center',
                  transformOrigin: '50% 0%',
                  transform: `rotate(${deg((360 * i) / count)}) translateY(${px(-r)})`,
                  fontFamily: ctx.font.family,
                  fontWeight: 800,
                  fontSize: px(fs),
                  color: ink,
                  whiteSpace: 'pre',
                },
              })),
            ],
            [spinAnim(D, num(ctx, 'turns', 0.3))],
          ),
          ...(label
            ? [
                {
                  text: label,
                  style: {
                    position: 'absolute',
                    left: px(-R),
                    top: px(-R * 0.3),
                    width: px(R * 2),
                    height: px(R * 0.6),
                    lineHeight: px(R * 0.6),
                    textAlign: 'center',
                    fontFamily: ctx.font.family,
                    fontWeight: 900,
                    fontSize: px(
                      Math.min(R * 0.42, (R * 0.95) / Math.max(1, glyphs(label) * 0.66)),
                    ),
                    color: ink,
                    letterSpacing: '-0.03em',
                    whiteSpace: 'nowrap',
                  },
                },
              ]
            : []),
        ],
        [pop(0, 700)],
      ),
    ];
  },
};

const crt: FxKind = {
  id: 'crt',
  label: lbl('CRT screen', 'CRT-екран', 'Ekran CRT', 'Ecran CRT', 'Pantalla CRT', 'CRT-экран'),
  category: 'ambient',
  z: 'front',
  duration: 5000,
  params: [P_OPACITY(0.35), P_COLOR('color', 'dark')],
  build(ctx): FxNode[] {
    const { height: H } = ctx.canvas;
    const D = ctx.duration;
    const u = unit(ctx);
    const a = clamp(num(ctx, 'opacity', 0.35), 0.05, 1);
    const c = color(ctx, 'color', 'dark');
    const line = Math.max(2, 4 * u);
    const band = H * 0.18;
    const P = Math.min(D, 2600);
    return [
      {
        style: full(ctx, {
          background: `repeating-linear-gradient(0deg, ${withAlpha(c, a)} 0px, ${withAlpha(c, a)} ${px(line / 2)}, transparent ${px(line / 2)}, transparent ${px(line)})`,
        }),
        anims: [
          {
            delay: 0,
            duration: Math.min(D, 120),
            iterations: Math.max(1, Math.floor(D / 120)),
            fill: 'both',
            easing: 'steps(2, end)',
            keyframes: [{ opacity: 1 }, { opacity: 0.85 }],
          },
        ],
      },
      {
        style: full(ctx, {
          height: px(band),
          background: `linear-gradient(180deg, transparent, ${withAlpha('#FFFFFF', 0.07)}, transparent)`,
        }),
        anims: [
          {
            delay: 0,
            duration: P,
            iterations: Math.max(1, Math.ceil(D / P)),
            fill: 'both',
            easing: 'linear',
            keyframes: [
              { transform: `translateY(${px(-band)})` },
              { transform: `translateY(${px(H)})` },
            ],
          },
        ],
      },
      {
        style: full(ctx, {
          background: `radial-gradient(ellipse at center, transparent 55%, ${withAlpha('#000000', a * 1.4)} 100%)`,
        }),
      },
    ];
  },
};

const lightLeak: FxKind = {
  id: 'light-leak',
  label: lbl(
    'Light leaks',
    'Засвітки плівки',
    'Prześwietlenia',
    'Scurgeri de lumină',
    'Fugas de luz',
    'Засветки плёнки',
  ),
  category: 'ambient',
  z: 'front',
  duration: 5000,
  params: [
    P_COUNT(3, 1, 6),
    P_COLOR('color', '#FF8A3D'),
    P_COLOR2('#FF3D7F'),
    P_COLOR3('#FFD23D'),
    P_OPACITY(0.55),
  ],
  build(ctx): FxNode[] {
    const { width: W, height: H } = ctx.canvas;
    const D = ctx.duration;
    const rnd = rng(ctx.seed);
    const n = Math.round(clamp(num(ctx, 'count', 3), 1, 6));
    const colors = [
      color(ctx, 'color', 'accent'),
      color(ctx, 'color2', 'accent2'),
      color(ctx, 'color3', 'light'),
    ];
    const a = clamp(num(ctx, 'opacity', 0.55), 0.05, 1);
    const S = Math.max(W, H) * 0.9;
    return Array.from({ length: n }, (_, i) => {
      const edgeX = i % 2 ? W - S * 0.35 : -S * 0.65;
      const y = rnd() * H - S / 2;
      return {
        style: {
          position: 'absolute',
          left: px(edgeX),
          top: px(y),
          width: px(S),
          height: px(S),
          borderRadius: '50%',
          background: `radial-gradient(circle, ${withAlpha(colors[i % 3], 0.95)} 0%, ${withAlpha(colors[i % 3], 0.35)} 35%, transparent 68%)`,
          mixBlendMode: 'screen',
        },
        anims: [
          {
            delay: 0,
            duration: D,
            fill: 'both',
            easing: 'ease-in-out',
            keyframes: [
              { opacity: 0, transform: 'translate(0px, 0px) scale(0.9)' },
              {
                opacity: a,
                transform: `translate(${px((rnd() - 0.5) * W * 0.2)}, ${px((rnd() - 0.5) * H * 0.3)}) scale(1.05)`,
                offset: 0.3 + rnd() * 0.2,
              },
              {
                opacity: a * 0.4,
                transform: `translate(${px((rnd() - 0.5) * W * 0.3)}, ${px((rnd() - 0.5) * H * 0.3)}) scale(1.15)`,
              },
            ],
          },
        ],
      };
    });
  },
};

export const ORIGINAL_FX_KINDS: FxKind[] = [
  particles3d,
  blobs,
  tunnel,
  sunburst,
  warp,
  wordWall,
  extrude,
  glitchText,
  neon,
  splitFlap,
  donut,
  lineChart,
  equalizer,
  ticker,
  countdown,
  cursor,
  shapeMorph,
  sticker,
  crt,
  lightLeak,
];
